import {
    VerticalStackStretch,
    useColors,
    componentStyles,
    SIZES,
    Button,
    ConditionalRenderContainer,
    ColorSchemes,
} from "@mallardbay/lib-react-components"
import React from "react"
import type { UIMessage } from "@ai-sdk/react"

import SmartScheduleConfirmationButtons from "~components/calendar/smart-schedule-ai-modal/smart-schedule-confirmation-buttons"
import { useChat } from "~components/shared/chat/chat-provider"
import {
    getAiSuggestionIdFromMessage,
    isMessageFromAi,
} from "~components/shared/chat/chat-helpers"
import ChatMessageParts from "~components/shared/chat/chat-message-parts"
import COPY from "~config/copy-constants"

interface Props {
    readonly message: UIMessage
}

export default function ChatMessage({ message }: Props) {
    const styles = useStyles({ message })
    const {
        aiAgent,
        selectedAiSuggestionId,
        setSelectedAiSuggestionId,
        lastSelectedAiSuggestionId,
        cancelSuggestion,
        acceptSuggestion,
        isLoadingAcceptSuggestion,
    } = useChat()
    const messageAiSuggestionId = getAiSuggestionIdFromMessage(message)

    const { shouldShowConfirmationButtons, shouldShowPreviewButton } =
        useUiState({
            selectedAiSuggestionId,
            messageAiSuggestionId,
            lastSelectedAiSuggestionId,
        })

    const onPreviewClick = () =>
        messageAiSuggestionId &&
        setSelectedAiSuggestionId(messageAiSuggestionId)

    return (
        <VerticalStackStretch style={styles.root}>
            <ChatMessageParts message={message} />
            <ConditionalRenderContainer shouldRender={shouldShowPreviewButton}>
                <Button
                    onClick={onPreviewClick}
                    isDisabled={Boolean(selectedAiSuggestionId)}
                    colorScheme={ColorSchemes.Brand}
                >
                    {COPY.PREVIEW_CHANGES}
                </Button>
            </ConditionalRenderContainer>
            <ConditionalRenderContainer
                shouldRender={shouldShowConfirmationButtons}
            >
                <SmartScheduleConfirmationButtons
                    aiAgent={aiAgent}
                    selectedAiSuggestionId={selectedAiSuggestionId}
                    cancelSuggestion={cancelSuggestion}
                    acceptSuggestion={acceptSuggestion}
                    isLoadingAcceptSuggestion={isLoadingAcceptSuggestion}
                />
            </ConditionalRenderContainer>
        </VerticalStackStretch>
    )
}

function useStyles({ message }: { message: UIMessage }) {
    const colors = useColors()
    const isAi = isMessageFromAi(message)

    return componentStyles({
        root: {
            borderRadius: "lg",
            textAlign: isAi ? "left" : "right",
            backgroundColor: isAi ? undefined : colors.bgTertiary,
            fontWeight: isAi ? "normal" : "bold",
            padding: isAi ? undefined : 4,
            "& ol": { paddingX: 4 },
            "& ul": { paddingX: 4 },
            "& pre": {
                backgroundColor: colors.bgSecondary,
                textWrap: "wrap",
                color: colors.secondaryText,
                fontStyle: "italic",
                padding: 2,
                borderRadius: "md",
                fontSize: SIZES.SM,
            },
        },
    })
}

function useUiState({
    selectedAiSuggestionId,
    messageAiSuggestionId,
    lastSelectedAiSuggestionId,
}: {
    selectedAiSuggestionId: string | null
    messageAiSuggestionId: string | null | undefined
    lastSelectedAiSuggestionId: string | null
}) {
    const isSuggestionSelected =
        Boolean(selectedAiSuggestionId) &&
        selectedAiSuggestionId === messageAiSuggestionId
    const hasBeenApplied =
        Boolean(messageAiSuggestionId) &&
        messageAiSuggestionId === lastSelectedAiSuggestionId
    const shouldShowPreviewButton =
        Boolean(messageAiSuggestionId) &&
        !isSuggestionSelected &&
        !hasBeenApplied

    return {
        shouldShowConfirmationButtons: isSuggestionSelected,
        shouldShowPreviewButton,
    }
}
